import React, {useEffect, useState} from 'react'
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {Box, IconButton, MenuItem, Select, Stack, Typography} from "@mui/material";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import MicIcon from '@mui/icons-material/Mic';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {LoadingButton} from '@mui/lab';
import {VoiceNoteService} from "crqlar-react-common/dist/services/voiceNote/voiceNote.service";
import {
    AUDIO_SECONDS_LIMIT,
    blobToBase64,
    defaultVoiceNoteState,
    SUBMIT_TIMEOUT_SECONDS_LIMIT,
    useGroupedPersistedState,
    VoiceNoteStatus
} from 'crqlar-react-common/dist/utils/hooks/voiceNote/usePersistedState';
import {
    extractCurrentStayDate,
    extractProfileAndRoom,
    renderAssignedErrorAlert,
    renderAssignedSuccessAlert,
    renderInitialInfoAlert,
    renderReceiveProfileSuggestionsErrorAlert,
    renderReceiveProfileSuggestionsSuccessAlert,
    renderTranscriptionTakingTooLongAlert
} from 'crqlar-react-common/dist/utils/hooks/voiceNote/voiceNote.util';
import {
    useOrganizationApps
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {formatTime} from "crqlar-react-common/dist/utils/date.util";
import { useOrganization } from "crqlar-react-common/dist/state/auth.reducer";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";


const VoiceNoteRecorder = () => {
    const {t} = useLocalize()

    const crmAppId = useOrganizationApps().find(app => app.name?.toLowerCase() === 'crm')?.id
    // State section

    const readOnlyUser = useReadOnly()

    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | undefined>(undefined)
    const [recordedAudio, setRecordedAudio] = useState<FormData | undefined>(undefined)

    const organizationId = useOrganization()?.id

    // Persisted state section
    const [voiceNoteState, setVoiceNoteState] = useGroupedPersistedState(organizationId)

    const updateVoiceNoteField = <K extends keyof typeof voiceNoteState>(
        key: K,
        value: typeof voiceNoteState[K] | ((prev: typeof voiceNoteState[K]) => typeof voiceNoteState[K])
    ) => {
        setVoiceNoteState(prev => ({
            ...prev,
            [key]: typeof value === 'function'
                ? (value as (prev: typeof voiceNoteState[K]) => typeof voiceNoteState[K])(prev[key])
                : value
        }))
    }

    // We use the current state variables here
    const seconds = voiceNoteState.seconds
    const voiceNoteStatus = voiceNoteState.voiceNoteStatus
    const voiceNote = voiceNoteState.voiceNote
    const suggestedProfiles = voiceNoteState.suggestedProfiles
    const chosenProfile = voiceNoteState.chosenProfile

    useEffect(() => {
        const loadAudioFromLocalStorage = async () => {
            const base64 = localStorage.getItem(`voiceNote_audio-${organizationId}`)
            if (!base64) return

            try {
                const blob = await fetch(base64).then(res => res.blob())
                const formData = new FormData()
                formData.append('file', blob, 'voice.webm')

                setRecordedAudio(formData)
            } catch (e) {
                console.error("Failed to restore recorded audio from localStorage", e)
            }
        }

        loadAudioFromLocalStorage()
    }, [])

    /*
      If it hits the limit of recording time, stop recording automatically
      Reason: Preventing long recordings which could be costly
   */
    useEffect(() => {
        if (seconds >= AUDIO_SECONDS_LIMIT) {
            mediaRecorder?.stop()
            updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.STOPPED)
        }
    }, [seconds])

    // Counting the seconds of recording
    useEffect(() => {
        let interval: NodeJS.Timeout
        if (voiceNoteStatus === VoiceNoteStatus.RECORDING) {
            interval = setInterval(() => updateVoiceNoteField('seconds', (prev: number) => prev + 1), 1000)
        }
        return () => clearInterval(interval)
    }, [voiceNoteStatus])

    // As soon as we get suggestions the first profile is assigned to chosen
    useEffect(() => {
        updateVoiceNoteField('chosenProfile', chosenProfile || suggestedProfiles[0] || undefined)
    }, [suggestedProfiles])

    // Handle logic section

    // Clear everything and reset the state
    const handleClearAudio = () => {
        setVoiceNoteState(defaultVoiceNoteState)
        setRecordedAudio(undefined)
        setMediaRecorder(undefined)
    }

    const handleStartRecording = async () => {
        setVoiceNoteState(defaultVoiceNoteState)
        updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.RECORDING)
        updateVoiceNoteField('seconds', 0)

        const stream = await navigator.mediaDevices.getUserMedia({audio: true})
        const recorder = new MediaRecorder(stream)

        const localChunks: BlobPart[] = []

        recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                localChunks.push(event.data)
            }
        }

        recorder.onstop = () => {
            const audioBlob = new Blob(localChunks, {type: 'audio/webm'})

            if (audioBlob.size === 0) {
                console.error("❌ Recorded audio is empty. Make sure recording lasted at least ~1 second.")
                return
            }

            const formData = new FormData();
            formData.append('file', audioBlob, 'voice.webm')

            setRecordedAudio(formData)

            blobToBase64(audioBlob).then((base64Audio) => {
                localStorage.setItem(`voiceNote_audio-${organizationId}`, base64Audio)
            })
        }

        recorder.start()
        setMediaRecorder(recorder)
    }

    const handleStopRecording = () => {
        updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.STOPPED)
        mediaRecorder?.stop()
    }

    const handleAssignVoiceNoteToProfile = () => {
        updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.ASSIGNING)
        updateVoiceNoteField('suggestedProfiles', [])

        VoiceNoteService.assignVoiceNote({
            voiceNoteId: voiceNote?.id,
            profileId: chosenProfile?.id
        })
            .then(res => {
                updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.ASSIGNED_SUCCESS)
                updateVoiceNoteField('voiceNote', res.voiceNote)
                setRecordedAudio(undefined)
            })
            .catch(err => {
                updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.ASSIGNED_ERROR)
            })
    }

    const handleGetProfileSuggestions = () => {
        updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.GETTING_PROFILE_SUGGESTIONS)
        let didTimeout = false

        const timeoutId = setTimeout(() => {
            didTimeout = true
            updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.TRANSCRIPTION_TAKING_TOO_LONG)
            // milliseconds format
        }, SUBMIT_TIMEOUT_SECONDS_LIMIT * 1000)

        VoiceNoteService.add(recordedAudio)
            .then(voiceNoteResponse => {
                if (didTimeout) return

                if (voiceNoteResponse.suggestedProfiles?.length) {
                    updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.RECEIVED_PROFILE_SUGGESTIONS_SUCCESS)
                } else {
                    updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.RECEIVED_PROFILE_SUGGESTIONS_ERROR)
                }

                clearTimeout(timeoutId)
                updateVoiceNoteField('voiceNote', {
                    ...voiceNote,
                    id: voiceNoteResponse.voiceNoteId,
                    originalTranscription: voiceNoteResponse.voiceNoteTranscription
                })
                updateVoiceNoteField('suggestedProfiles', voiceNoteResponse?.suggestedProfiles ?? [])
            })
            .catch(err => {
                if (didTimeout) return

                updateVoiceNoteField('voiceNoteStatus', VoiceNoteStatus.RECEIVED_PROFILE_SUGGESTIONS_ERROR)
                clearTimeout(timeoutId)
            })
    }

    // UI section

    const renderSuggestedProfiles = () => {
        if (!suggestedProfiles.length) return null

        return (
            <Stack
                direction={'row'}
                gap={2}
                alignItems={'center'}
                justifyContent={'center'}
                flexWrap={'wrap'}
            >
                <Select
                    value={chosenProfile?.id || suggestedProfiles[0]?.id || ''}
                    onChange={(e) => {
                        const selectedProfile = suggestedProfiles.find(p => p.id === e.target.value);
                        updateVoiceNoteField('chosenProfile', selectedProfile || undefined);
                    }}
                    fullWidth
                    size="small"
                >
                    {suggestedProfiles.map(profile => (
                        <MenuItem
                            key={profile.id}
                            value={profile.id}
                            id={`profile-option-${profile.id}`}
                        >
                            <Typography variant={'h6'}>
                                {extractProfileAndRoom(profile, t)}
                                {extractCurrentStayDate(profile, t)}
                            </Typography>

                        </MenuItem>
                    ))}
                </Select>
            </Stack>
        )
    }

    const renderActions = () => {
        return (
            <>
                <Stack
                    direction={'row'}
                    gap={2}
                    alignItems={'center'}
                    justifyContent={'center'}
                >
                    <LoadingButton
                        variant="contained"
                        loading={voiceNoteStatus === VoiceNoteStatus.GETTING_PROFILE_SUGGESTIONS ||
                            voiceNoteStatus === VoiceNoteStatus.ASSIGNING}
                        color={'secondary'}
                        disabled={
                            voiceNoteStatus !== VoiceNoteStatus.RECEIVED_PROFILE_SUGGESTIONS_SUCCESS &&
                            voiceNoteStatus !== VoiceNoteStatus.STOPPED
                        }
                        /*
                            The button has 2 handlers -> one for getting the suggestions,
                            another one for assigning the voice note to profile preferences or allergies
                        */
                        onClick={suggestedProfiles.length ? handleAssignVoiceNoteToProfile : handleGetProfileSuggestions}
                        sx={{
                            fontSize: {xs: '0.9rem', sm: '1rem'},
                            paddingX: {xs: 3, sm: 4},
                            paddingY: {xs: 1, sm: 1.5},
                        }}
                    >
                        {suggestedProfiles.length ? t('Submit') : t('Get profile suggestions')}
                    </LoadingButton>

                    {
                        // When something is recorded
                        voiceNoteStatus === VoiceNoteStatus.STOPPED &&
                        <Typography
                            sx={{
                                fontSize: {xs: '0.9rem', sm: '1rem', md: '1.1rem'},
                                color: CommonThemeColors.sys.primary.main,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: CommonThemeColors.sys.primary.hover,
                                }
                            }}
                            onClick={handleClearAudio}
                        >
                            {t('Clear')}
                        </Typography>
                    }

                </Stack>
            </>
        )
    }

    // All the alerts depend on the current voice note creation state
    const determineAlert = () => {
        switch (voiceNoteStatus) {
            case VoiceNoteStatus.IDLE:
            case VoiceNoteStatus.RECORDING:
            case VoiceNoteStatus.STOPPED:
            case VoiceNoteStatus.GETTING_PROFILE_SUGGESTIONS:
                return renderInitialInfoAlert(t)
            case VoiceNoteStatus.RECEIVED_PROFILE_SUGGESTIONS_SUCCESS:
                return renderReceiveProfileSuggestionsSuccessAlert(voiceNote, t)
            case VoiceNoteStatus.RECEIVED_PROFILE_SUGGESTIONS_ERROR:
                return renderReceiveProfileSuggestionsErrorAlert(voiceNote, t)
            case VoiceNoteStatus.ASSIGNING:
                return renderReceiveProfileSuggestionsSuccessAlert(voiceNote, t)
            case VoiceNoteStatus.ASSIGNED_SUCCESS:
                return renderAssignedSuccessAlert(chosenProfile, voiceNote, t, crmAppId as string)
            case VoiceNoteStatus.ASSIGNED_ERROR:
                return renderAssignedErrorAlert(t)
            case VoiceNoteStatus.TRANSCRIPTION_TAKING_TOO_LONG:
                return renderTranscriptionTakingTooLongAlert(t)
            default:
                return null
        }
    }

    const renderRecorder = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    width: '100%',
                }}
            >
                <IconButton
                    onClick={handleStartRecording}
                    disabled={voiceNoteStatus === VoiceNoteStatus.RECORDING || readOnlyUser}
                    sx={{
                        color: CommonThemeColors.sys.primary.main,
                        border: `1px solid ${CommonThemeColors.sys.primary.main}`,
                        borderRadius: 1,
                        padding: {xs: 1, sm: 1.5, md: 2},
                        '& svg': {
                            fontSize: {xs: 24, sm: 32, md: 40},
                        },
                    }}
                >
                    <MicIcon/>
                    <Typography
                        sx={{
                            ml: 1,
                            fontSize: {xs: '0.9rem', sm: '1rem', md: '1.1rem'},
                        }}
                    >
                        {t('New recording')}
                    </Typography>
                </IconButton>

                {
                    (voiceNoteStatus === VoiceNoteStatus.RECORDING || voiceNoteStatus === VoiceNoteStatus.STOPPED) &&
                    <>
                        <GraphicEqIcon
                            sx={{
                                color: CommonThemeColors.sys.primary.main,
                                fontSize: {xs: 24, sm: 32, md: 40},
                            }}
                        />

                        <Typography sx={{fontSize: {xs: '0.9rem', sm: '1rem', md: '1.1rem'}}}>
                            {formatTime(seconds)}
                        </Typography>

                        <IconButton
                            disabled={voiceNoteStatus !== VoiceNoteStatus.RECORDING}
                            onClick={handleStopRecording}
                            sx={{
                                color: CommonThemeColors.sys.primary.main,
                                padding: {xs: 1, sm: 1.5, md: 2},
                                '& svg': {
                                    fontSize: {xs: 24, sm: 32, md: 40},
                                },
                            }}
                        >
                            <StopCircleIcon/>
                        </IconButton>
                    </>
                }

            </Box>
        )
    }

    return (
        <ScreenWrapper>

            <Stack spacing={3} alignItems="center" width="100%" mt={10}>
                {renderRecorder()}
                {renderActions()}
                {voiceNoteStatus === VoiceNoteStatus.RECEIVED_PROFILE_SUGGESTIONS_SUCCESS && renderSuggestedProfiles()}
                {determineAlert()}
            </Stack>

        </ScreenWrapper>

    )
}

export default VoiceNoteRecorder
